/**
 * Juiced Header JavaScript
 * Handles all interactive functionality for the header, mobile menu, search, and cart drawer
 */

class JuicedHeader {
  constructor() {
    this.header = document.querySelector('.header-section');
    this.mobileToggle = document.querySelector('.mobile__toggle--menu');
    this.mobileMenu = document.getElementById('MobileMenu');
    this.mobileOverlay = document.getElementById('MobileMenuOverlay');
    this.searchToggle = document.querySelector('[data-action="open-search"]');
    this.searchBar = document.getElementById('SearchBar');
    this.searchInput = document.getElementById('SearchInput');
    this.searchClose = document.querySelector('[data-action="close-search"]');
    this.cartToggle = document.querySelector('[data-action="open-cart"]');
    this.cartDrawer = document.getElementById('CartDrawer');
    this.cartOverlay = document.getElementById('CartDrawerOverlay');
    this.cartClose = document.querySelector('[data-action="close-cart"]');

    this.lastScrollTop = 0;
    this.isMenuOpen = false;
    this.isSearchOpen = false;
    this.isCartOpen = false;

    this.init();
  }

  init() {
    this.bindEvents();
    this.initStickyHeader();
    this.initMegamenu();
    this.initMobileMenu();
    this.initSearch();
    this.initCartDrawer();
  }

  bindEvents() {
    // Mobile menu toggle
    if (this.mobileToggle) {
      this.mobileToggle.addEventListener('click', () => this.toggleMobileMenu());
      this.mobileToggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleMobileMenu();
        }
      });
    }

    // Mobile menu overlay
    if (this.mobileOverlay) {
      this.mobileOverlay.addEventListener('click', () => this.closeMobileMenu());
    }

    // Mobile menu close button
    const mobileClose = document.querySelector('[data-action="close-menu"]');
    if (mobileClose) {
      mobileClose.addEventListener('click', () => this.closeMobileMenu());
    }

    // Search toggle
    if (this.searchToggle) {
      this.searchToggle.addEventListener('click', () => this.toggleSearch());
    }

    // Search close
    if (this.searchClose) {
      this.searchClose.addEventListener('click', () => this.closeSearch());
    }

    // Cart toggle
    if (this.cartToggle) {
      this.cartToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleCart();
      });
    }

    // Cart overlay
    if (this.cartOverlay) {
      this.cartOverlay.addEventListener('click', () => this.closeCart());
    }

    // Cart close
    if (this.cartClose) {
      this.cartClose.addEventListener('click', () => this.closeCart());
    }

    // Escape key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.isMenuOpen) this.closeMobileMenu();
        if (this.isSearchOpen) this.closeSearch();
        if (this.isCartOpen) this.closeCart();
      }
    });

    // Resize handler
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1024 && this.isMenuOpen) {
        this.closeMobileMenu();
      }
    });
  }

  // ===========================================
  // Sticky Header
  // ===========================================
  initStickyHeader() {
    if (!this.header) return;

    const stickyType = this.header.dataset.sticky;

    if (stickyType === 'never') return;

    if (stickyType === 'scroll-up') {
      window.addEventListener('scroll', () => this.handleScrollUpSticky());
    }
  }

  handleScrollUpSticky() {
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

    if (currentScroll > this.lastScrollTop && currentScroll > 100) {
      // Scrolling down
      this.header.classList.add('header-section--hidden');
    } else {
      // Scrolling up
      this.header.classList.remove('header-section--hidden');
    }

    this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
  }

  // ===========================================
  // Megamenu (Desktop)
  // ===========================================
  initMegamenu() {
    const menuItems = document.querySelectorAll('.header__menu-item');

    menuItems.forEach(item => {
      const link = item.querySelector('.header__menu-link--has-megamenu');
      const megamenu = item.querySelector('.megamenu');

      if (!link || !megamenu) return;

      // Click to open (nav v3). Previously this was mouseenter/mouseleave,
      // which made the panel unreachable — moving the pointer off the item
      // toward the panel fired mouseleave and closed it before you arrived.
      //
      // It cannot be neutralised from an override script: mouseenter does not
      // bubble and has no capture path through ancestors, so a listener on the
      // <ul> never sees it, and on the item itself listeners fire in
      // registration order (this file registers first). It has to change here.
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = item.classList.contains('active-megamenu');
        this.closeAllMegamenus();
        if (!isOpen) this.openMegamenu(item);
      });

      // Keyboard accessibility
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isExpanded = link.getAttribute('aria-expanded') === 'true';

          if (isExpanded) {
            this.closeMegamenu(item);
          } else {
            this.closeAllMegamenus();
            this.openMegamenu(item);
          }
        }
      });

      // NOTE: no blur-to-close on the last focusable element. With click-to-
      // open that fires while you are still using the panel (e.g. tabbing out
      // of the final link, or the browser moving focus) and closes it under
      // you. Outside click and Escape below handle closing.
    });

    // Close when clicking anywhere outside the header.
    document.addEventListener('click', (e) => {
      const header = e.target.closest && e.target.closest('.header-section');
      if (header) return;
      this.closeAllMegamenus();
    });

    // Close on Escape, returning focus to the trigger.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      const open = document.querySelector('.header__menu-item.active-megamenu');
      if (!open) return;
      this.closeAllMegamenus();
      const trigger = open.querySelector('.header__menu-link');
      if (trigger) trigger.focus();
    });
  }

  openMegamenu(item) {
    const link = item.querySelector('.header__menu-link--has-megamenu');
    const megamenu = item.querySelector('.megamenu');

    if (link) link.setAttribute('aria-expanded', 'true');
    if (megamenu) {
      megamenu.classList.add('active');
      // Check if dropdown would overflow right edge of viewport
      this.positionDropdown(megamenu);
    }
    item.classList.add('active-megamenu');
  }

  positionDropdown(dropdown) {
    // Reset position first
    dropdown.classList.remove('dropdown--flip-right');
    dropdown.classList.remove('dropdown--flip-left');

    // Get the dropdown's position
    const rect = dropdown.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Check if dropdown overflows right edge
    if (rect.right > viewportWidth - 20) {
      dropdown.classList.add('dropdown--flip-right');
    }
    // Check if dropdown overflows left edge
    else if (rect.left < 20) {
      dropdown.classList.add('dropdown--flip-left');
    }
  }

  closeMegamenu(item) {
    const link = item.querySelector('.header__menu-link--has-megamenu');
    const megamenu = item.querySelector('.megamenu');

    if (link) link.setAttribute('aria-expanded', 'false');
    if (megamenu) megamenu.classList.remove('active');
    item.classList.remove('active-megamenu');
  }

  closeAllMegamenus() {
    const menuItems = document.querySelectorAll('.header__menu-item');
    menuItems.forEach(item => this.closeMegamenu(item));
  }

  // ===========================================
  // Mobile Menu
  // ===========================================
  initMobileMenu() {
    // Mobile submenu toggles
    const toggleButtons = document.querySelectorAll('[data-menu-toggle]');

    toggleButtons.forEach(button => {
      button.addEventListener('click', () => {
        const menuItem = button.closest('.mobile-menu-item');
        const isActive = menuItem.classList.contains('active');

        // Close all other submenus
        document.querySelectorAll('.mobile-menu-item.active').forEach(item => {
          if (item !== menuItem) {
            item.classList.remove('active');
            const btn = item.querySelector('[data-menu-toggle]');
            if (btn) btn.setAttribute('aria-expanded', 'false');
            const submenu = item.querySelector('.mobile-submenu');
            if (submenu) submenu.setAttribute('aria-hidden', 'true');
          }
        });

        // Toggle current submenu
        if (isActive) {
          menuItem.classList.remove('active');
          button.setAttribute('aria-expanded', 'false');
          const submenu = menuItem.querySelector('.mobile-submenu');
          if (submenu) submenu.setAttribute('aria-hidden', 'true');
        } else {
          menuItem.classList.add('active');
          button.setAttribute('aria-expanded', 'true');
          const submenu = menuItem.querySelector('.mobile-submenu');
          if (submenu) submenu.setAttribute('aria-hidden', 'false');
        }
      });
    });
  }

  toggleMobileMenu() {
    if (this.isMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  openMobileMenu() {
    this.isMenuOpen = true;

    if (this.header) this.header.classList.add('active');
    if (this.mobileMenu) {
      this.mobileMenu.classList.add('active');
      this.mobileMenu.setAttribute('aria-hidden', 'false');
    }
    if (this.mobileOverlay) this.mobileOverlay.classList.add('active');
    if (this.mobileToggle) this.mobileToggle.setAttribute('aria-expanded', 'true');

    document.body.classList.add('menu-open');
    document.body.style.overflow = 'hidden';

    // Focus first focusable element
    setTimeout(() => {
      const firstFocusable = this.mobileMenu?.querySelector('a, button');
      if (firstFocusable) firstFocusable.focus();
    }, 100);
  }

  closeMobileMenu() {
    this.isMenuOpen = false;

    if (this.header) this.header.classList.remove('active');
    if (this.mobileMenu) {
      this.mobileMenu.classList.remove('active');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
    }
    if (this.mobileOverlay) this.mobileOverlay.classList.remove('active');
    if (this.mobileToggle) this.mobileToggle.setAttribute('aria-expanded', 'false');

    document.body.classList.remove('menu-open');
    document.body.style.overflow = '';

    // Return focus to toggle
    if (this.mobileToggle) this.mobileToggle.focus();
  }

  // ===========================================
  // Search
  // ===========================================
  initSearch() {
    if (!this.searchInput) return;

    // Predictive search
    let searchTimeout;
    this.searchInput.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      const query = e.target.value.trim();

      if (query.length >= 2) {
        searchTimeout = setTimeout(() => {
          this.performPredictiveSearch(query);
        }, 300);
      } else {
        this.hideSearchResults();
      }
    });

    // Form submit
    const searchForm = document.getElementById('SearchForm');
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        const query = this.searchInput.value.trim();
        if (!query) {
          e.preventDefault();
        }
      });
    }
  }

  toggleSearch() {
    if (this.isSearchOpen) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    this.isSearchOpen = true;

    if (this.searchBar) {
      this.searchBar.classList.add('active');
      this.searchBar.setAttribute('aria-hidden', 'false');
    }
    if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', 'true');

    // Focus search input
    setTimeout(() => {
      if (this.searchInput) this.searchInput.focus();
    }, 100);
  }

  closeSearch() {
    this.isSearchOpen = false;

    if (this.searchBar) {
      this.searchBar.classList.remove('active');
      this.searchBar.setAttribute('aria-hidden', 'true');
    }
    if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', 'false');

    // Clear search
    if (this.searchInput) this.searchInput.value = '';
    this.hideSearchResults();

    // Return focus
    if (this.searchToggle) this.searchToggle.focus();
  }

  async performPredictiveSearch(query) {
    const resultsContainer = document.getElementById('SearchResultsContainer');
    if (!resultsContainer) return;

    resultsContainer.style.display = 'flex';

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article,page&resources[limit]=4`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      this.renderSearchResults(data.resources?.results || data.results || {}, query);
    } catch (error) {
      console.error('Predictive search error:', error);
      resultsContainer.innerHTML = `
        <div class="search-empty-state">
          <p class="search-empty-message">No Results Found</p>
        </div>
      `;
      resultsContainer.style.display = 'flex';
    }
  }

  renderSearchResults(results, query) {
    const resultsContainer = document.getElementById('SearchResultsContainer');
    if (!resultsContainer) return;

    const hasProducts = results.products?.length > 0;
    const hasCollections = results.collections?.length > 0;
    const hasArticles = results.articles?.length > 0;
    const hasPages = results.pages?.length > 0;
    const hasAnyResults = hasProducts || hasCollections || hasArticles || hasPages;

    if (!hasAnyResults) {
      resultsContainer.innerHTML = `
        <div class="search-empty-state">
          <p class="search-empty-message">No Results Found</p>
        </div>
      `;
      resultsContainer.style.display = 'flex';
      return;
    }

    const toggleSvg = `<button class="search-result-group-toggle" aria-expanded="true">
        <svg class="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>`;

    // Build results HTML using search.css class names
    let html = '';

    // Products
    if (hasProducts) {
      html += `
        <div class="search-result-group">
          <div class="search-result-group-header">
            <h3 class="search-result-group-title">Products</h3>
            ${toggleSvg}
          </div>
          <div class="search-result-items">
            ${results.products.map(product => `
              <div class="search-result-item">
                <a href="${product.url}" class="search-result-link">${product.title}</a>
              </div>
            `).join('')}
          </div>
        </div>`;
    }

    // Collections
    if (hasCollections) {
      html += `
        <div class="search-result-group">
          <div class="search-result-group-header">
            <h3 class="search-result-group-title">Collections</h3>
            ${toggleSvg}
          </div>
          <div class="search-result-items">
            ${results.collections.map(collection => `
              <div class="search-result-item">
                <a href="${collection.url}" class="search-result-link">${collection.title}</a>
              </div>
            `).join('')}
          </div>
        </div>`;
    }

    // Articles
    if (hasArticles) {
      html += `
        <div class="search-result-group">
          <div class="search-result-group-header">
            <h3 class="search-result-group-title">Articles</h3>
            ${toggleSvg}
          </div>
          <div class="search-result-items">
            ${results.articles.map(article => `
              <div class="search-result-item">
                <a href="${article.url}" class="search-result-link">${article.title}</a>
              </div>
            `).join('')}
          </div>
        </div>`;
    }

    // Pages
    if (hasPages) {
      html += `
        <div class="search-result-group">
          <div class="search-result-group-header">
            <h3 class="search-result-group-title">Pages</h3>
            ${toggleSvg}
          </div>
          <div class="search-result-items">
            ${results.pages.map(page => `
              <div class="search-result-item">
                <a href="${page.url}" class="search-result-link">${page.title}</a>
              </div>
            `).join('')}
          </div>
        </div>`;
    }

    // Replace the results container content directly
    resultsContainer.innerHTML = html;
    resultsContainer.style.display = 'flex';

    // Add toggle listeners for collapsible groups
    resultsContainer.querySelectorAll('.search-result-group-toggle').forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        const group = toggle.closest('.search-result-group');
        if (group) {
          group.classList.toggle('collapsed');
          toggle.setAttribute('aria-expanded', !group.classList.contains('collapsed'));
        }
      });
    });
  }

  hideSearchResults() {
    const resultsContainer = document.getElementById('SearchResultsContainer');
    if (resultsContainer) {
      resultsContainer.style.display = 'none';
    }
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  // ===========================================
  // Cart Drawer
  // ===========================================
  initCartDrawer() {
    // Quantity buttons
    document.addEventListener('click', async (e) => {
      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.dataset.action;
      const line = target.dataset.line;

      if (action === 'increase-quantity' && line) {
        await this.updateCartQuantity(line, 1);
      } else if (action === 'decrease-quantity' && line) {
        await this.updateCartQuantity(line, -1);
      } else if (action === 'remove-item' && line) {
        await this.removeCartItem(line);
      }
    });

    // Quantity input change
    document.addEventListener('change', async (e) => {
      if (e.target.matches('[data-quantity-input]')) {
        const line = e.target.dataset.line;
        const quantity = parseInt(e.target.value, 10);
        if (line && quantity >= 0) {
          await this.setCartQuantity(line, quantity);
        }
      }
    });

    // Listen for cart updates from other sources
    document.addEventListener('cart:refresh', () => this.refreshCart());
  }

  toggleCart() {
    if (this.isCartOpen) {
      this.closeCart();
    } else {
      this.openCart();
    }
  }

  openCart() {
    this.isCartOpen = true;

    if (this.cartDrawer) {
      this.cartDrawer.classList.add('active');
      this.cartDrawer.setAttribute('aria-hidden', 'false');
    }
    if (this.cartOverlay) this.cartOverlay.classList.add('active');

    document.body.style.overflow = 'hidden';

    // Focus first focusable element
    setTimeout(() => {
      const firstFocusable = this.cartDrawer?.querySelector('a, button');
      if (firstFocusable) firstFocusable.focus();
    }, 100);
  }

  closeCart() {
    this.isCartOpen = false;

    if (this.cartDrawer) {
      this.cartDrawer.classList.remove('active');
      this.cartDrawer.setAttribute('aria-hidden', 'true');
    }
    if (this.cartOverlay) this.cartOverlay.classList.remove('active');

    document.body.style.overflow = '';

    // Return focus
    if (this.cartToggle) this.cartToggle.focus();
  }

  async updateCartQuantity(line, change) {
    const input = document.querySelector(`[data-quantity-input][data-line="${line}"]`);
    if (!input) return;

    const currentQuantity = parseInt(input.value, 10);
    const newQuantity = Math.max(0, currentQuantity + change);

    await this.setCartQuantity(line, newQuantity);
  }

  async setCartQuantity(line, quantity) {
    try {
      // Each engraved nameplate belongs to exactly one separately configured
      // bike. Never let this drawer silently raise that parent's quantity.
      if (quantity > 1) {
        const currentResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
        if (!currentResponse.ok) throw new Error('Unable to check personalized cart quantity');
        const currentCart = await currentResponse.json();
        const parent = currentCart.items?.[Number(line) - 1];
        const hasPersonalizedChild = parent && currentCart.items.some((item) =>
          item.parent_relationship?.parent_key === parent.key &&
          Boolean(item.properties?.Name?.trim())
        );
        if (hasPersonalizedChild) {
          const field = document.querySelector(`[data-quantity-input][data-line="${line}"]`);
          if (field) field.value = parent.quantity;
          window.alert('Each customized bike needs its own nameplate. Add another bike separately on the product page to enter its engraving.');
          return;
        }
      }
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: parseInt(line, 10),
          quantity: quantity
        })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const cart = await response.json();
      this.updateCartUI(cart);

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (error) {
      console.error('Cart update error:', error);
    }
  }

  async removeCartItem(line) {
    await this.setCartQuantity(line, 0);
  }

  async refreshCart() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) throw new Error('Failed to fetch cart');

      const cart = await response.json();
      this.updateCartUI(cart);
    } catch (error) {
      console.error('Cart refresh error:', error);
    }
  }

  updateCartUI(cart) {
    // The Juiced header counts purchasable top-level items, not nested add-ons.
    // Shopify's native cart/checkout item_count remains unchanged.
    const cartDisplayCount = Array.isArray(cart.items)
      ? cart.items.reduce((count, item) => {
          const hasParent = Boolean(item.parent_relationship?.parent_key);
          return count + (hasParent ? 0 : (Number(item.quantity) || 0));
        }, 0)
      : (Number(cart.item_count) || 0);

    const cartCounts = document.querySelectorAll('[data-cart-count]');
    cartCounts.forEach(el => {
      el.textContent = cartDisplayCount;
      el.dataset.cartCount = cartDisplayCount;
    });

    // Update cart icon state
    const cartIcons = document.querySelectorAll('.header-actions__cart-icon');
    cartIcons.forEach(icon => {
      if (cartDisplayCount > 0) {
        icon.classList.add('header-actions__cart-icon--has-cart');
      } else {
        icon.classList.remove('header-actions__cart-icon--has-cart');
      }
    });

    // Update cart total
    const cartTotal = document.getElementById('CartTotal');
    if (cartTotal) {
      cartTotal.textContent = this.formatMoney(cart.total_price);
    }

    // Refresh cart drawer content via section rendering
    this.refreshCartDrawerContent();
  }

  async refreshCartDrawerContent() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=header-juiced`);
      if (!response.ok) return;

      const sections = await response.json();

      // Parse and update cart drawer content
      if (sections['header-juiced']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['header-juiced'], 'text/html');

        const newCartContent = doc.querySelector('.cart-drawer-content');
        const currentCartContent = document.querySelector('.cart-drawer-content');

        if (newCartContent && currentCartContent) {
          currentCartContent.innerHTML = newCartContent.innerHTML;
        }

        const newCartFooter = doc.querySelector('.cart-drawer-footer');
        const currentCartFooter = document.querySelector('.cart-drawer-footer');

        if (newCartFooter && currentCartFooter) {
          currentCartFooter.innerHTML = newCartFooter.innerHTML;
          currentCartFooter.className = newCartFooter.className;
        }
      }
    } catch (error) {
      console.error('Cart drawer refresh error:', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new JuicedHeader());
} else {
  new JuicedHeader();
}

// Export for module usage
export { JuicedHeader };
